import * as Blockly from 'blockly';

/**
 * RETOCAR BLOCK DEFINITIONS (LEGO SPIKE STYLE)
 * Khối lệnh chuẩn mực mô phỏng hình học LEGO Education SPIKE Prime với zelos renderer.
 * Hỗ trợ Snapping biến số qua ValueInput, Shadow blocks và bản địa hóa 100% tiếng Việt.
 */
export const RETO_COLOR_OPTIONS = [
  ['🔴 Đỏ (RED)', 'RED'],
  ['🟢 Xanh lá (GREEN)', 'GREEN'],
  ['🔵 Xanh dương (BLUE)', 'BLUE'],
  ['🟡 Vàng (YELLOW)', 'YELLOW'],
  ['⚪ Trắng (WHITE)', 'WHITE'],
  ['⚫ Đen (BLACK)', 'BLACK'],
  ['🟤 Nâu (BROWN)', 'BROWN'],
  ['🟣 Tím (PURPLE)', 'PURPLE']
];

export function registerRetoCarBlocks() {
  // Cấu hình kích thước chuẩn cho nút bấm Flyout (Tạo một biến số..., Tạo một danh sách...)
  if (Blockly.FlyoutButton) {
    Blockly.FlyoutButton.TEXT_MARGIN_X = 20;
    Blockly.FlyoutButton.TEXT_MARGIN_Y = 11;
    Blockly.FlyoutButton.BORDER_RADIUS = 8;
  }

  // Tùy biến dropdown của FieldVariable: Tách bạch rõ ràng giữa "biến số" và "danh sách"
  if (Blockly.FieldVariable && !Blockly.FieldVariable._retoblockPatched) {
    const originalDropdownCreate = Blockly.FieldVariable.dropdownCreate;
    Blockly.FieldVariable.dropdownCreate = function () {
      const options = originalDropdownCreate.call(this);
      const varTypes = typeof this.getVariableTypes === 'function' ? this.getVariableTypes() : [];
      const isList = (Array.isArray(varTypes) && varTypes.includes('list')) ||
                     (this.variable && this.variable.getType() === 'list') ||
                     (this.defaultType === 'list');
      const varName = typeof this.getText === 'function' ? this.getText() : '';

      if (isList && Array.isArray(options)) {
        return options.map(([text, id]) => {
          if (id === 'RENAME_VARIABLE_ID') {
            return ['Đổi tên danh sách...', id];
          }
          if (id === 'DELETE_VARIABLE_ID') {
            return [`Xóa danh sách "${varName}"`, id];
          }
          return [text, id];
        });
      }
      return options;
    };
    Blockly.FieldVariable._retoblockPatched = true;
  }

  // Tùy biến kiểm tra tương thích ngàm kết nối (ConnectionChecker):
  // Đảm bảo sự kiện [Khi phát hiện màu...] NÊN và CHỈ cho phép kéo Biến số vào
  if (Blockly.ConnectionChecker && !Blockly.ConnectionChecker._retoblockPatched) {
    const origDoTypeChecks = Blockly.ConnectionChecker.prototype.doTypeChecks;
    Blockly.ConnectionChecker.prototype.doTypeChecks = function (a, b) {
      const connA = a;
      const connB = b;
      const inputConn = connA.type === Blockly.INPUT_VALUE ? connA : (connB.type === Blockly.INPUT_VALUE ? connB : null);
      const outputConn = inputConn === connA ? connB : connA;

      if (inputConn && outputConn) {
        const parentBlock = inputConn.getSourceBlock();
        const childBlock = outputConn.getSourceBlock();

        // Sự kiện [Khi phát hiện màu...]: Chỉ cho phép Biến số hoặc Shadow Color Picker
        if (parentBlock && parentBlock.type === 'when_color_detected' && inputConn.getParentInput()?.name === 'COLOR') {
          if (childBlock) {
            // Cho phép shadow block chọn màu mặc định
            if (childBlock.type === 'reto_color_picker') return true;
            // Cho phép biến số (variables_get) nhưng TUYỆT ĐỐI KHÔNG cho phép danh sách
            if (childBlock.type === 'variables_get') {
              const varField = childBlock.getField('VAR');
              const v = varField ? varField.getVariable() : null;
              return !v || v.getType() !== 'list';
            }
            // Mọi khối khác (danh sách, toán tử, cảm biến...) đều bị từ chối
            return false;
          }
        }
      }

      return origDoTypeChecks.call(this, a, b);
    };
    Blockly.ConnectionChecker._retoblockPatched = true;
  }

  // ===========================================================================
  // 1. SỰ KIỆN (EVENTS)
  // ===========================================================================
  Blockly.Blocks['when_program_starts'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khi chương trình bắt đầu');
      this.setNextStatement(true, null);
      this.setStyle('event_blocks');
      this.setTooltip('Bắt đầu chạy kịch bản robot');
    }
  };

  Blockly.Blocks['when_color_detected'] = {
    init: function () {
      this.appendValueInput('COLOR')
        .setCheck(['Color', 'String', 'Variable'])
        .appendField('khi phát hiện màu');
      this.setNextStatement(true, null);
      this.setStyle('event_blocks');
      this.setTooltip('Kích hoạt khi cảm biến TCS34725 phát hiện màu chỉ định (chỉ cho phép kéo biến số vào)');
    }
  };

  Blockly.Blocks['when_button_event'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khi nút')
        .appendField(new Blockly.FieldDropdown([
          ['BT1', '1'],
          ['BT2', '2']
        ]), 'BUTTON')
        .appendField('được')
        .appendField(new Blockly.FieldDropdown([
          ['nhấn', 'PRESSED'],
          ['nhả', 'RELEASED']
        ]), 'ACTION');
      this.setNextStatement(true, null);
      this.setStyle('event_blocks');
      this.setTooltip('Kích hoạt khi nút bấm vật lý trên robot thay đổi trạng thái');
    }
  };

  Blockly.Blocks['when_timer_greater'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khi thời gian chạy >');
      this.appendValueInput('VALUE')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('giây');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setStyle('event_blocks');
      this.setTooltip('Kích hoạt khi đồng hồ bấm giờ vượt qua ngưỡng thời gian');
    }
  };

  Blockly.Blocks['when_condition_true'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khi');
      this.appendValueInput('CONDITION')
        .setCheck('Boolean');
      this.setInputsInline(true);
      this.setNextStatement(true, null);
      this.setStyle('event_blocks');
      this.setTooltip('Kích hoạt khi điều kiện logic trở thành đúng');
    }
  };

  // ===========================================================================
  // 2. ĐIỀU KHIỂN (CONTROL)
  // ===========================================================================
  Blockly.Blocks['wait_seconds'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('chờ');
      this.appendValueInput('SECONDS')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('giây');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Tạm dừng chương trình trong khoảng thời gian xác định (hỗ trợ kéo thả biến số)');
    }
  };

  Blockly.Blocks['repeat_times'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('lặp lại');
      this.appendValueInput('TIMES')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('lần');
      this.appendStatementInput('DO')
        .appendField('thực hiện');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Lặp lại các khối lệnh bên trong theo số lần chỉ định');
    }
  };

  Blockly.Blocks['forever_loop'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('lặp lại mãi mãi ↺');
      this.appendStatementInput('DO')
        .appendField('thực hiện');
      this.setPreviousStatement(true, null);
      this.setNextStatement(false);
      this.setStyle('control_blocks');
      this.setTooltip('Vòng lặp vô tận');
    }
  };

  Blockly.Blocks['if_then'] = {
    init: function () {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('nếu');
      this.appendStatementInput('DO')
        .appendField('thì');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Kiểm tra điều kiện nếu đúng thì thực hiện');
    }
  };

  Blockly.Blocks['if_then_else'] = {
    init: function () {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('nếu');
      this.appendStatementInput('DO')
        .appendField('thì');
      this.appendStatementInput('ELSE')
        .appendField('không thì');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Kiểm tra điều kiện rẽ nhánh');
    }
  };

  Blockly.Blocks['wait_until'] = {
    init: function () {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('đợi cho đến khi');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Tạm dừng kịch bản cho đến khi điều kiện logic thỏa mãn');
    }
  };

  Blockly.Blocks['repeat_until'] = {
    init: function () {
      this.appendValueInput('CONDITION')
        .setCheck('Boolean')
        .appendField('lặp lại cho đến khi');
      this.appendStatementInput('DO')
        .appendField('thực hiện');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Lặp lại các khối lệnh bên trong cho đến khi điều kiện trở thành đúng');
    }
  };

  Blockly.Blocks['loop_continue'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('bỏ qua lần lặp này');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Bỏ qua các lệnh còn lại của bước lặp hiện tại và chuyển sang bước kế tiếp (continue)');
    }
  };

  Blockly.Blocks['loop_break'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('thoát khỏi vòng lặp');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('control_blocks');
      this.setTooltip('Dừng ngay lập tức và thoát ra khỏi vòng lặp hiện tại (break)');
    }
  };

  // ===========================================================================
  // 3. CHUYỂN ĐỘNG & SERVO (MOVEMENT & ACTUATOR)
  // ===========================================================================
  // Shadow picker blocks (Dropdown as Value Input)
  Blockly.Blocks['reto_direction_picker'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['tiến ▲', 'FORWARD'],
          ['lùi ▼', 'BACKWARD'],
          ['quay trái ◄', 'LEFT'],
          ['quay phải ►', 'RIGHT']
        ]), 'DIRECTION');
      this.setOutput(true, 'String');
      this.setStyle('movement_blocks');
      this.setTooltip('Chọn hướng di chuyển');
    }
  };

  Blockly.Blocks['reto_motor_side_picker'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['trái', 'LEFT'],
          ['phải', 'RIGHT']
        ]), 'MOTOR');
      this.setOutput(true, 'String');
      this.setStyle('movement_blocks');
      this.setTooltip('Chọn động cơ trái hoặc phải');
    }
  };

  Blockly.Blocks['reto_move_direction_speed'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('di chuyển')
        .appendField(new Blockly.FieldDropdown([
          ['tiến ▲', 'FORWARD'],
          ['lùi ▼', 'BACKWARD'],
          ['quay trái ◄', 'LEFT'],
          ['quay phải ►', 'RIGHT']
        ]), 'DIRECTION');
      this.appendValueInput('SPEED')
        .setCheck('Number')
        .appendField('với tốc độ');
      this.appendDummyInput()
        .appendField('%');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('movement_blocks');
      this.setTooltip('Điều khiển cả 2 bánh robot di chuyển');
    }
  };

  Blockly.Blocks['reto_move_stop'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('dừng di chuyển');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('movement_blocks');
      this.setTooltip('Phanh dừng ngay lập tức cả hai động cơ');
    }
  };

  Blockly.Blocks['reto_motor_single'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('động cơ')
        .appendField(new Blockly.FieldDropdown([
          ['trái', 'LEFT'],
          ['phải', 'RIGHT']
        ]), 'MOTOR')
        .appendField('quay')
        .appendField(new Blockly.FieldDropdown([
          ['tiến ▲', 'FORWARD'],
          ['lùi ▼', 'BACKWARD']
        ]), 'DIRECTION');
      this.appendValueInput('SPEED')
        .setCheck('Number')
        .appendField('tốc độ');
      this.appendDummyInput()
        .appendField('%');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('movement_blocks');
      this.setTooltip('Điều khiển độc lập công suất từng bánh xe');
    }
  };

  // Đặt góc động cơ Servo (Sáp nhập vào Chuyển động)
  Blockly.Blocks['reto_set_servo_angle'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('đặt góc động cơ servo');
      this.appendValueInput('ANGLE')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('độ (0 - 180°)');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('movement_blocks');
      this.setTooltip('Điều khiển góc quay của động cơ RC Servo (hỗ trợ kéo thả biến số)');
    }
  };

  // Khối tương thích ngược: reto_set_arm_angle
  Blockly.Blocks['reto_set_arm_angle'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('đặt góc cánh tay nâng');
      this.appendValueInput('ANGLE')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('độ (0 - 180°)');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('movement_blocks');
      this.setTooltip('Điều khiển góc quay của RC Servo càng nâng hàng');
    }
  };

  // ===========================================================================
  // 4. CẢM BIẾN (SENSORS)
  // ===========================================================================
  Blockly.Blocks['reto_distance_less_than'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khoảng cách gần hơn');
      this.appendValueInput('DISTANCE')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('cm ?');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setStyle('sensor_blocks');
      this.setTooltip('Kiểm tra khoảng cách vật cản phía trước có gần hơn ngưỡng chỉ định không');
    }
  };

  Blockly.Blocks['reto_get_distance'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('khoảng cách siêu âm (cm)');
      this.setOutput(true, 'Number');
      this.setStyle('sensor_blocks');
      this.setTooltip('Giá trị khoảng cách đọc từ cảm biến siêu âm HC-SR04 (2 - 400 cm)');
    }
  };

  Blockly.Blocks['reto_line_detected'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('mắt dò đường')
        .appendField(new Blockly.FieldDropdown([
          ['S1 (Trái)', '1'],
          ['S2 (Giữa)', '2'],
          ['S3 (Phải)', '3']
        ]), 'SENSOR')
        .appendField('= vạch')
        .appendField(new Blockly.FieldDropdown([
          [Blockly.Msg['RETOCAR_LINE_COLOR_BLACK'] || 'đen', 'BLACK'],
          [Blockly.Msg['RETOCAR_LINE_COLOR_WHITE'] || 'trắng', 'WHITE']
        ]), 'LINE_COLOR')
        .appendField('?');
      this.setOutput(true, 'Boolean');
      this.setStyle('sensor_blocks');
      this.setTooltip(Blockly.Msg['RETOCAR_LINE_DETECTED_TOOLTIP'] || 'Kiểm tra mắt hồng ngoại có phát hiện vạch đen hoặc vạch trắng không');
    }
  };

  // Shadow pickers for Sensors & Feedback
  Blockly.Blocks['reto_color_picker'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown(RETO_COLOR_OPTIONS), 'COLOR');
      this.setOutput(true, 'String');
      this.setStyle('sensor_blocks');
      this.setTooltip('Chọn màu sắc');
    }
  };

  Blockly.Blocks['reto_button_picker'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['BT1', '1'],
          ['BT2', '2']
        ]), 'BUTTON');
      this.setOutput(true, ['String', 'Number']);
      this.setStyle('sensor_blocks');
      this.setTooltip('Chọn nút bấm BT1 hoặc BT2');
    }
  };

  Blockly.Blocks['reto_color_is'] = {
    init: function () {
      this.appendValueInput('COLOR')
        .setCheck('String')
        .appendField('phát hiện màu');
      this.appendDummyInput()
        .appendField('?');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setStyle('sensor_blocks');
      this.setTooltip('So sánh màu sắc nhận diện từ cảm biến TCS34725/GY-33 (hỗ trợ kéo thả biến số)');
    }
  };

  Blockly.Blocks['reto_detected_color_id'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('màu được phát hiện');
      this.setOutput(true, 'Number');
      this.setStyle('sensor_blocks');
      this.setTooltip('Trả về mã số màu từ 1 đến 8 (1: Đỏ, 2: Xanh lá, 3: Xanh dương, 4: Vàng, 5: Trắng, 6: Đen, 7: Nâu, 8: Tím, 0: Chưa rõ)');
    }
  };

  Blockly.Blocks['reto_button_pressed'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('nút')
        .appendField(new Blockly.FieldDropdown([
          ['BT1', '1'],
          ['BT2', '2']
        ]), 'BUTTON')
        .appendField('được')
        .appendField(new Blockly.FieldDropdown([
          [Blockly.Msg['RETOCAR_BUTTON_STATE_PRESSED'] || 'nhấn', 'PRESSED'],
          [Blockly.Msg['RETOCAR_BUTTON_STATE_RELEASED'] || 'nhả', 'RELEASED']
        ]), 'STATE')
        .appendField('?');
      this.setOutput(true, 'Boolean');
      this.setStyle('sensor_blocks');
      this.setTooltip(Blockly.Msg['RETOCAR_BUTTON_PRESSED_TOOLTIP'] || 'Kiểm tra trạng thái phím BT1 hoặc BT2 trên robot. Trả về Đúng (True) nếu nút được chọn đang ở trạng thái tương ứng (đang được bấm giữ hoặc đang thả tay).');
    }
  };

  Blockly.Blocks['reto_get_light'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('cường độ ánh sáng (%)');
      this.setOutput(true, 'Number');
      this.setStyle('sensor_blocks');
      this.setTooltip('Cường độ ánh sáng từ cảm biến quang trở (0 - 100%)');
    }
  };

  Blockly.Blocks['reto_timer'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('thời gian hiện tại');
      this.setOutput(true, 'Number');
      this.setStyle('sensor_blocks');
      this.setTooltip('Thời gian trôi qua tính bằng giây kể từ khi bắt đầu hoặc đặt lại đồng hồ');
    }
  };

  Blockly.Blocks['reto_reset_timer'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('đặt lại đồng hồ bấm giờ');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('sensor_blocks');
      this.setTooltip('Khởi động lại đồng hồ bấm giờ về 0');
    }
  };

  // ===========================================================================
  // 5. PHẢN HỒI & TÍN HIỆU (FEEDBACK)
  // ===========================================================================
  Blockly.Blocks['reto_set_rgb'] = {
    init: function () {
      this.appendValueInput('COLOR')
        .setCheck('String')
        .appendField('bật đèn RGB màu');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip('Đặt màu phát sáng cho cụm đèn LED RGB (hỗ trợ kéo thả biến số)');
    }
  };

  // SVG ô tròn màu trực quan (Đỏ, Xanh lá, Xanh dương) chuẩn màu sắc, viền trắng và hiệu ứng ánh sáng nổi bật
  const RED_DOT_ICON = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48Y2lyY2xlIGN4PSIxMCIgY3k9IjEwIiByPSI4IiBmaWxsPSIjRkYxRTI3IiBzdHJva2U9IiNGRkZGRkYiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjcuNSIgY3k9IjcuNSIgcj0iMi4yIiBmaWxsPSIjRkZGRkZGIiBmaWxsLW9wYWNpdHk9IjAuNDUiLz48L3N2Zz4=';
  const GREEN_DOT_ICON = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48Y2lyY2xlIGN4PSIxMCIgY3k9IjEwIiByPSI4IiBmaWxsPSIjMDBFNjc2IiBzdHJva2U9IiNGRkZGRkYiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjcuNSIgY3k9IjcuNSIgcj0iMi4yIiBmaWxsPSIjRkZGRkZGIiBmaWxsLW9wYWNpdHk9IjAuNDUiLz48L3N2Zz4=';
  const BLUE_DOT_ICON = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIj48Y2lyY2xlIGN4PSIxMCIgY3k9IjEwIiByPSI4IiBmaWxsPSIjMDA4MEZGIiBzdHJva2U9IiNGRkZGRkYiIHN0cm9rZS13aWR0aD0iMiIvPjxjaXJjbGUgY3g9IjcuNSIgY3k9IjcuNSIgcj0iMi4yIiBmaWxsPSIjRkZGRkZGIiBmaWxsLW9wYWNpdHk9IjAuNDUiLz48L3N2Zz4=';

  Blockly.Blocks['retocar_set_rgb_percent'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(Blockly.Msg['RETOCAR_SET_RGB_PERCENT_TITLE'] || 'bật đèn RGB');
      this.appendValueInput('RED')
        .setCheck('Number')
        .appendField(new Blockly.FieldImage(RED_DOT_ICON, 18, 18, Blockly.Msg['RETOCAR_SET_RGB_PERCENT_RED'] || 'Đỏ'));
      this.appendDummyInput()
        .appendField('%');
      this.appendValueInput('GREEN')
        .setCheck('Number')
        .appendField(new Blockly.FieldImage(GREEN_DOT_ICON, 18, 18, Blockly.Msg['RETOCAR_SET_RGB_PERCENT_GREEN'] || 'Xanh lá'));
      this.appendDummyInput()
        .appendField('%');
      this.appendValueInput('BLUE')
        .setCheck('Number')
        .appendField(new Blockly.FieldImage(BLUE_DOT_ICON, 18, 18, Blockly.Msg['RETOCAR_SET_RGB_PERCENT_BLUE'] || 'Xanh dương'));
      this.appendDummyInput()
        .appendField('%');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip(Blockly.Msg['RETOCAR_SET_RGB_PERCENT_TOOLTIP'] || 'Pha màu tùy ý bằng cách điều chỉnh độ sáng từ 0% đến 100% cho từng bóng LED Đỏ, Xanh lá, Xanh dương. Có thể dùng biến số hoặc phép toán.');
    }
  };
  Blockly.Blocks['reto_set_rgb_percent'] = Blockly.Blocks['retocar_set_rgb_percent'];

  Blockly.Blocks['reto_rgb_off'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('tắt đèn RGB');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip('Tắt hoàn toàn đèn LED RGB');
    }
  };

  Blockly.Blocks['reto_set_indicator'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('xi-nhan')
        .appendField(new Blockly.FieldDropdown([
          ['Trái', 'LEFT'],
          ['Phải', 'RIGHT'],
          ['Cả hai', 'BOTH']
        ]), 'SIDE')
        .appendField(new Blockly.FieldDropdown([
          ['Bật', 'ON'],
          ['Tắt', 'OFF']
        ]), 'STATE');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip('Điều khiển đèn xi-nhan báo hiệu');
    }
  };

  Blockly.Blocks['reto_buzzer_tone'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('phát âm thanh tần số');
      this.appendValueInput('FREQ')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('Hz');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip('Phát âm thanh còi liên tục với tần số xác định');
    }
  };

  Blockly.Blocks['reto_buzzer_off'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('tắt âm thanh');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('feedback_blocks');
      this.setTooltip('Ngắt âm thanh còi buzzer ngay lập tức');
    }
  };

  // ===========================================================================
  // 6. TOÁN TỬ & PHÉP TÍNH (OPERATORS)
  // ===========================================================================
  Blockly.Blocks['operator_arithmetic'] = {
    init: function () {
      this.appendValueInput('A');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['+', 'ADD'],
          ['-', 'MINUS'],
          ['×', 'MULTIPLY'],
          ['÷', 'DIVIDE']
        ]), 'OP');
      this.appendValueInput('B');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setStyle('operator_blocks');
      this.setTooltip('Thực hiện các phép toán cơ bản (+, -, ×, ÷)');
    }
  };

  Blockly.Blocks['operator_compare'] = {
    init: function () {
      this.appendValueInput('A');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['=', '=='],
          ['≠', '!='],
          ['<', '<'],
          ['≤', '<='],
          ['>', '>'],
          ['≥', '>=']
        ]), 'OP');
      this.appendValueInput('B');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setStyle('operator_blocks');
      this.setTooltip('So sánh hai giá trị (=, ≠, <, ≤, >, ≥)');
    }
  };

  Blockly.Blocks['operator_logic'] = {
    init: function () {
      this.appendValueInput('A')
        .setCheck('Boolean');
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['và (AND)', 'and'],
          ['hoặc (OR)', 'or']
        ]), 'OP');
      this.appendValueInput('B')
        .setCheck('Boolean');
      this.setOutput(true, 'Boolean');
      this.setStyle('operator_blocks');
      this.setTooltip('Phép toán logic');
    }
  };

  Blockly.Blocks['operator_not'] = {
    init: function () {
      this.appendValueInput('BOOL')
        .setCheck('Boolean')
        .appendField('không phải');
      this.setOutput(true, 'Boolean');
      this.setStyle('operator_blocks');
      this.setTooltip('Phép phủ định logic');
    }
  };

  Blockly.Blocks['operator_random'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('lấy ngẫu nhiên từ');
      this.appendValueInput('FROM')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('đến');
      this.appendValueInput('TO')
        .setCheck('Number');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setStyle('operator_blocks');
      this.setTooltip('Sinh số nguyên ngẫu nhiên');
    }
  };

  Blockly.Blocks['operator_between'] = {
    init: function () {
      this.appendValueInput('VAL')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('nằm trong khoảng từ');
      this.appendValueInput('MIN')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('đến');
      this.appendValueInput('MAX')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('?');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setStyle('operator_blocks');
      this.setTooltip('Kiểm tra giá trị có nằm trong đoạn từ MIN đến MAX hay không');
    }
  };

  Blockly.Blocks['operator_mod'] = {
    init: function () {
      this.appendValueInput('A')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('chia lấy dư');
      this.appendValueInput('B')
        .setCheck('Number');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setStyle('operator_blocks');
      this.setTooltip('Chia lấy phần dư của hai số (mod / %)');
    }
  };

  Blockly.Blocks['operator_round'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('làm tròn');
      this.appendValueInput('NUM')
        .setCheck('Number');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setStyle('operator_blocks');
      this.setTooltip('Làm tròn số đến số nguyên gần nhất');
    }
  };

  Blockly.Blocks['operator_math_op'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldDropdown([
          ['abs (giá trị tuyệt đối)', 'abs'],
          ['floor (làm tròn xuống)', 'floor'],
          ['ceiling (làm tròn lên)', 'ceiling'],
          ['sqrt (căn bậc hai)', 'sqrt'],
          ['sin', 'sin'],
          ['cos', 'cos'],
          ['tan', 'tan'],
          ['asin', 'asin'],
          ['acos', 'acos'],
          ['atan', 'atan'],
          ['ln (logarit tự nhiên)', 'ln'],
          ['log (logarit thập phân)', 'log'],
          ['e ^ (hàm mũ e^x)', 'exp'],
          ['10 ^ (lũy thừa 10^x)', 'pow10']
        ]), 'OP')
        .appendField('của');
      this.appendValueInput('NUM')
        .setCheck('Number');
      this.setInputsInline(true);
      this.setOutput(true, 'Number');
      this.setStyle('operator_blocks');
      this.setTooltip('Thực hiện các hàm số học và lượng giác khoa học chuẩn LEGO SPIKE');
    }
  };

  // ===========================================================================
  // 7. DANH SÁCH DỮ LIỆU (LISTS / ARRAYS) - 100% TIẾNG VIỆT
  // ===========================================================================
  Blockly.Blocks['reto_list_reporter'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'VAR');
      this.setOutput(true, 'Array');
      this.setStyle('list_blocks');
      this.setTooltip('Trả về danh sách dữ liệu');
    },
    customContextMenu: function (options) {
      const varField = this.getField('VAR');
      const varName = varField ? varField.getText() : '';
      const variable = varField ? varField.getVariable() : null;
      if (variable && options) {
        options.unshift({
          text: 'Đổi tên danh sách...',
          enabled: true,
          callback: () => {
            if (Blockly.Variables && typeof Blockly.Variables.renameVariable === 'function') {
              Blockly.Variables.renameVariable(this.workspace, variable);
            }
          }
        });
        options.unshift({
          text: `Xóa danh sách "${varName}"`,
          enabled: true,
          callback: () => {
            if (Blockly.Variables && typeof Blockly.Variables.deleteVariable === 'function') {
              Blockly.Variables.deleteVariable(this.workspace, variable);
            }
          }
        });
      }
    }
  };

  Blockly.Blocks['reto_list_create_empty'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('tạo danh sách rỗng');
      this.setOutput(true, 'Array');
      this.setStyle('list_blocks');
      this.setTooltip('Tạo một danh sách rỗng []');
    }
  };

  Blockly.Blocks['reto_list_add'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('thêm');
      this.appendValueInput('ITEM');
      this.appendDummyInput()
        .appendField('vào')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Thêm phần tử vào cuối danh sách');
    }
  };

  Blockly.Blocks['reto_list_delete'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('xóa phần tử thứ');
      this.appendValueInput('INDEX')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Xóa phần tử tại vị trí xác định trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_delete_all'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('xóa tất cả phần tử của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Xóa sạch toàn bộ phần tử trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_insert'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('chèn');
      this.appendValueInput('ITEM');
      this.appendDummyInput()
        .appendField('vào vị trí');
      this.appendValueInput('INDEX')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Chèn một phần tử vào vị trí xác định trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_replace'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('thay thế phần tử thứ');
      this.appendValueInput('INDEX')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST')
        .appendField('bằng');
      this.appendValueInput('ITEM');
      this.setInputsInline(true);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Gán giá trị mới cho phần tử tại vị trí trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_get'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('phần tử thứ');
      this.appendValueInput('INDEX')
        .setCheck('Number');
      this.appendDummyInput()
        .appendField('của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setInputsInline(true);
      this.setOutput(true, null);
      this.setStyle('list_blocks');
      this.setTooltip('Lấy giá trị của phần tử tại vị trí trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_length'] = {
    init: function () {
      this.appendDummyInput()
        .appendField('độ dài của')
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST');
      this.setOutput(true, 'Number');
      this.setStyle('list_blocks');
      this.setTooltip('Số lượng phần tử trong danh sách');
    }
  };

  Blockly.Blocks['reto_list_contains'] = {
    init: function () {
      this.appendDummyInput()
        .appendField(new Blockly.FieldVariable('danh sách', null, ['list'], 'list'), 'LIST')
        .appendField('có chứa');
      this.appendValueInput('ITEM');
      this.appendDummyInput()
        .appendField('?');
      this.setInputsInline(true);
      this.setOutput(true, 'Boolean');
      this.setStyle('list_blocks');
      this.setTooltip('Kiểm tra xem danh sách có chứa phần tử hay không');
    }
  };

  // ===========================================================================
  // CHUẨN LEGO SPIKE / SCRATCH: DÀN HÀNG NGANG CÁC BIẾN CỦA KHỐI THỦ TỤC (MY BLOCKS)
  // & TỰ ĐỘNG GẮN SHADOW BLOCK MATH_NUMBER CHO PHÉP NHẬP GIÁ TRỊ TỰ DO
  // ===========================================================================
  function ensureProcedureCallShadows(block) {
    if (!block || !block.workspace || block.workspace.isClearing) return;
    setTimeout(() => {
      if (!block.workspace || block.workspace.isClearing || !block.inputList) return;
      for (let i = 0; i < block.inputList.length; i++) {
        const input = block.inputList[i];
        if (input && input.name && input.name.startsWith('ARG')) {
          const conn = input.connection;
          if (conn && !conn.targetBlock()) {
            try {
              const shadowBlock = block.workspace.newBlock('math_number');
              shadowBlock.setFieldValue('0', 'NUM');
              shadowBlock.setShadow(true);
              shadowBlock.initSvg();
              shadowBlock.render();
              conn.connect(shadowBlock.outputConnection);
            } catch (e) {
              // Ignore if block is in flyout or workspace disposing
            }
          }
        }
      }
    }, 0);
  }

  if (Blockly.Blocks['procedures_callnoreturn']) {
    const origCallNoReturnInit = Blockly.Blocks['procedures_callnoreturn'].init;
    Blockly.Blocks['procedures_callnoreturn'].init = function () {
      origCallNoReturnInit.call(this);
      this.setInputsInline(true);
      ensureProcedureCallShadows(this);
    };

    const origUpdateShape = Blockly.Blocks['procedures_callnoreturn'].updateShape_;
    if (origUpdateShape) {
      Blockly.Blocks['procedures_callnoreturn'].updateShape_ = function () {
        origUpdateShape.call(this);
        this.setInputsInline(true);
        ensureProcedureCallShadows(this);
      };
    }
  }

  if (Blockly.Blocks['procedures_callreturn']) {
    const origCallReturnInit = Blockly.Blocks['procedures_callreturn'].init;
    Blockly.Blocks['procedures_callreturn'].init = function () {
      origCallReturnInit.call(this);
      this.setInputsInline(true);
      ensureProcedureCallShadows(this);
    };

    const origUpdateShapeReturn = Blockly.Blocks['procedures_callreturn'].updateShape_;
    if (origUpdateShapeReturn) {
      Blockly.Blocks['procedures_callreturn'].updateShape_ = function () {
        origUpdateShapeReturn.call(this);
        this.setInputsInline(true);
        ensureProcedureCallShadows(this);
      };
    }
  }
}
